import { Country } from "../types/common";

export function isStringNullOrEmpty(value: string | null | undefined) {
  return !value || value === "";
}

const formatCurrency = (amount: number, country: Country = Country.ES) => {
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency:
      country === Country.SE ? "SEK" : country === Country.NO ? "NOK" : "EUR"
  }).format(amount ?? 0);
};

export { formatCurrency };

/**
 * Formats a plain amount with the currency symbol used in the overview
 * ("12 345 kr" / "12 345 €"). Digit grouping follows the application's
 * country, not the caseworker's browser locale, so the output is the same
 * for every user. Returns null for missing values so overview rows can drop
 * the line entirely.
 */
export const formatAmount = (
  amount: number | null | undefined,
  country?: Country
): string | null => {
  if (amount === null || amount === undefined || Number.isNaN(Number(amount))) {
    return null;
  }
  let locale: string;
  let symbol: string;
  switch (country) {
    case Country.FI:
      locale = "fi-FI";
      symbol = "€";
      break;
    case Country.ES:
      locale = "es-ES";
      symbol = "€";
      break;
    case Country.NO:
      locale = "nb-NO";
      symbol = "kr";
      break;
    default:
      // Country.SE and unknown/missing country
      locale = "sv-SE";
      symbol = "kr";
      break;
  }
  const roundedAmount = Math.round(Number(amount));
  return `${new Intl.NumberFormat(locale).format(roundedAmount)} ${symbol}`;
};

export function formatInterestRate(value?: number): string {
  if (value === undefined || value === null || Number.isNaN(value)) {
    return "-";
  }
  try {
    if (typeof value === "string") {
      value = Number.parseFloat(value);
    }

    return `${value.toFixed(2)} %`;
  } catch {
    return String(value);
  }
}

export function formatDateTime(date: string) {
  // Date format options for all queue items.
  const options: Intl.DateTimeFormatOptions = {
    dateStyle: "medium", // "full", "long", "medium", or "short"
    timeStyle: "short", // "full", "long", "medium", or "short"
    hour12: false
  };

  return new Intl.DateTimeFormat("en-GB", options)
    .format(new Date(Date.parse(date)))
    .replace(",", " - ");
}

export const convertChToPx = (ch: number | undefined) => {
  if (!ch) {
    return 0;
  }
  // Assuming 1ch = 8px
  return ch * 8;
};

export const convertVwToPx = (vw: number) => {
  const viewportWidth = window.innerWidth;
  return (vw / 100) * viewportWidth;
};
